import { useState, useEffect, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { apiClient } from '../api/client';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_CARD,
} from './legacy-ball/table-management/designTokens';

interface ReviewerContentEditorProps {
  submissionId: string;
  currentMemberId: string | null;
  userRole: string;
}

interface ReviewerContentItem {
  id: string;
  submissionId: string;
  memberId: string;
  memberName: string;
  body: string;
  version: number;
  createdAt: string;
}

const TOOLBAR_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

export function ReviewerContentEditor({
  submissionId,
  currentMemberId,
  userRole,
}: ReviewerContentEditorProps) {
  const [editorContent, setEditorContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [allContent, setAllContent] = useState<ReviewerContentItem[]>([]);

  const fetchContent = useCallback(async () => {
    try {
      const data = await apiClient<ReviewerContentItem[]>(
        `/weekend-booklet/submissions/${submissionId}/reviewer-content`,
      );
      setAllContent(data);

      // Pre-populate editor with latest version for the current reviewer
      if (currentMemberId) {
        const myContent = data.filter((item) => item.memberId === currentMemberId);
        if (myContent.length > 0) {
          // Data is ordered by version DESC, so first match is the latest
          setEditorContent(myContent[0].body);
        }
      }
    } catch {
      // If no content exists yet, that's fine — start with empty editor
    } finally {
      setLoading(false);
    }
  }, [submissionId, currentMemberId]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  async function handleSave() {
    if (!editorContent.trim() || editorContent === '<p><br></p>') {
      setErrorMsg('Content cannot be empty.');
      setSuccessMsg('');
      return;
    }

    setSaving(true);
    setErrorMsg('');
    setSuccessMsg('');

    try {
      await apiClient(
        `/weekend-booklet/submissions/${submissionId}/reviewer-content`,
        {
          method: 'POST',
          body: { body: editorContent },
        },
      );
      setSuccessMsg('Content saved!');
      // Refresh the content list to update version history
      await fetchContent();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setErrorMsg(apiErr.message || 'Failed to save content. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: '1rem', color: TEXT_MUTED, fontSize: '0.85rem' }}>
        Loading reviewer content...
      </div>
    );
  }

  // If currentMemberId is null (organizer/admin viewing without being a reviewer),
  // show all content as read-only panels, no editor
  if (!currentMemberId) {
    const reviewerGroups = groupByReviewer(allContent);
    return (
      <div style={styles.container}>
        <h4 style={styles.heading}>Reviewer Content</h4>
        {reviewerGroups.length === 0 && (
          <p style={styles.placeholder}>No reviewer content yet.</p>
        )}
        {reviewerGroups.map((group) => (
          <div key={group.memberId} style={styles.readOnlyPanel}>
            <h5 style={styles.panelHeading}>{group.memberName}</h5>
            <div
              style={styles.readOnlyContent}
              dangerouslySetInnerHTML={{ __html: group.latestBody }}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h4 style={styles.heading}>Your Content</h4>

      <div style={styles.editorWrapper}>
        <ReactQuill
          theme="snow"
          value={editorContent}
          onChange={setEditorContent}
          modules={TOOLBAR_MODULES}
          placeholder="Compose your content here..."
        />
      </div>

      <div style={styles.actions}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            ...styles.saveButton,
            backgroundColor: saving ? '#9ca3af' : PRIMARY,
            cursor: saving ? 'not-allowed' : 'pointer',
          }}
          aria-label="Save reviewer content"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>

      {successMsg && <p style={styles.success}>{successMsg}</p>}
      {errorMsg && <p style={styles.error}>{errorMsg}</p>}
    </div>
  );
}

// Helper: group content items by reviewer, taking only the latest version per reviewer
function groupByReviewer(
  items: ReviewerContentItem[],
): { memberId: string; memberName: string; latestBody: string }[] {
  const map = new Map<
    string,
    { memberId: string; memberName: string; latestBody: string }
  >();

  for (const item of items) {
    if (!map.has(item.memberId)) {
      map.set(item.memberId, {
        memberId: item.memberId,
        memberName: item.memberName,
        latestBody: item.body,
      });
    }
  }

  return Array.from(map.values());
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    marginTop: '1rem',
    padding: '1rem',
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
  },
  heading: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: TEXT_DARK,
    marginTop: 0,
    marginBottom: '0.75rem',
  },
  editorWrapper: {
    marginBottom: '0.75rem',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '0.5rem',
  },
  saveButton: {
    padding: '0.4rem 1rem',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  success: {
    fontSize: '0.8rem',
    color: '#16a34a',
    margin: '0.25rem 0 0 0',
  },
  error: {
    fontSize: '0.8rem',
    color: '#dc2626',
    margin: '0.25rem 0 0 0',
  },
  placeholder: {
    fontSize: '0.85rem',
    color: TEXT_MUTED,
    fontStyle: 'italic',
  },
  readOnlyPanel: {
    marginBottom: '0.75rem',
    padding: '0.75rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    backgroundColor: '#f9fafb',
  },
  panelHeading: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: TEXT_DARK,
    marginTop: 0,
    marginBottom: '0.5rem',
  },
  readOnlyContent: {
    fontSize: '0.85rem',
    color: TEXT_DARK,
    lineHeight: 1.5,
  },
};
